import React, { useContext, useEffect, useState } from 'react';
import { Refresh } from '@mui/icons-material';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import {
  IHistoryRecalc,
  useRecalcRollbackMutation,
  useHistoryRecalcCreatedSubscription,
  useHistoryRecalcUpdatedSubscription,
  useGetHistoryRecalcsQuery
} from '../../../../generated/graphql';
import { canUserCreate } from '../../../../utils/rightUtils';
import { renderDate } from '../../../../utils/styleUtils';
import { Colors } from '../../../../utils/theme';
import { Box, Button } from '@mui/material';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';
import InnerLoader from '../../../Common/InnerLoader';
import DenseTable from '../../../Common/Tables/DenseTable';
import RecalcForm from './RecalcForm';

interface IProps {
  stockId: string;
}

const RecalcsTab: React.FunctionComponent<IProps> = ({ stockId }: IProps) => {
  const [allData, setAllData] = useState<IHistoryRecalc[]>([]);
  const [openCreateRecalcForm, setOpenCreateRecalcForm] = useState(false);
  const [historyRecalc, setHistoryRecalc] = useState<IHistoryRecalc | null>(null);
  const [recalcRollback] = useRecalcRollbackMutation();
  const { dispatch, state } = useContext(AppContext);
  const canCreate = canUserCreate(state.user, Types.Stock);
  const createRecalcSubscription = useHistoryRecalcCreatedSubscription({
    variables: { stock_id: stockId }
  });
  const updateRecalcSubscription = useHistoryRecalcUpdatedSubscription({
    variables: { stock_id: stockId }
  });

  const { data, loading } = useGetHistoryRecalcsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      stock_id: parseInt(stockId as string)
    }
  });

  useEffect(() => {
    if (data && data.result) {
      setAllData(data.result as IHistoryRecalc[]);
    }
  }, [data]);

  useEffect(() => {
    if (createRecalcSubscription.data?.result) {
      addHistoryRecalc(createRecalcSubscription.data?.result as IHistoryRecalc);
    }
    if (updateRecalcSubscription.data?.result) {
      const updated = updateRecalcSubscription.data.result as IHistoryRecalc;
      updateHistoryRecalc(updated);
    }
  }, [createRecalcSubscription.data, updateRecalcSubscription.data]);

  const handleRollback = (rowData: IHistoryRecalc) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to rollback?`,
        agreeAction: executeRollback,
        params: rowData
      }
    });
  };

  const updateHistoryRecalc = (historyRecalc: IHistoryRecalc) => {
    setAllData(allData.map((k) => (k.id === historyRecalc.id ? historyRecalc : k)));
  };

  const executeRollback = (rowData: IHistoryRecalc) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    recalcRollback({
      variables: { id: rowData.id },
      update(_, { data }) {
        updateHistoryRecalc(data as unknown as IHistoryRecalc);
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: 'Recalc Rollbacked Successfully'
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const columns = [
    { title: 'Details', sorting: false, width: 81 },
    {
      title: 'Start Day',
      field: 'start_date',
      width: 133,
      type: 'date',
      render: (rowData: IHistoryRecalc) => renderDate(rowData.start_date)
    },
    {
      title: 'End Day',
      field: 'end_date',
      width: 146,
      type: 'date',
      render: (rowData: IHistoryRecalc) => renderDate(rowData.end_date)
    },
    { title: 'Field ', field: 'column_to_recalc', width: 624 },
    { title: 'Operator ', field: 'operator.value', width: 137 },
    { title: 'Factor ', field: 'value_to_calc_with', width: 127 },
    {
      title: 'RollBack',
      sorting: false,
      width: 72,
      // eslint-disable-next-line react/display-name
      render: (rowData: IHistoryRecalc) => (
        <Button
          style={{ height: '38px', transform: 'scaleX(-1)' }}
          onClick={() => handleRollback(rowData)}
          disabled={rowData.is_rollbacked || !canCreate}
        >
          <Refresh
            style={{
              color: rowData.is_rollbacked || !canCreate ? Colors.dark_gray : Colors.skyBlue
            }}
          />
        </Button>
      )
    }
  ] as IHeadCell[];

  const handleClose = (saved: boolean, isNew?: boolean, historyRecalcInput?: IHistoryRecalc) => {
    if (saved && historyRecalcInput) {
      if (isNew) {
        addHistoryRecalc(historyRecalcInput);
      }
    }
    setHistoryRecalc(null);
    setOpenCreateRecalcForm(false);
  };

  const addHistoryRecalc = (input: IHistoryRecalc) => {
    const existing = allData.find((d) => d.id === input.id);
    if (!existing) {
      setAllData([input, ...allData]);
    }
  };

  const handleDetails = (rowData: IHistoryRecalc) => {
    setHistoryRecalc(rowData);
    setOpenCreateRecalcForm(true);
  };

  return (
    <Box>
      {loading ? (
        <InnerLoader />
      ) : (
        <Box className="print\:w-full" sx={{ pt: '25px' }}>
          <DenseTable
            columns={columns}
            data={allData}
            handleDetails={handleDetails}
            handleRemove={() => true}
            toolbarBackground={Colors.white}
            createButtonProps={{
              id: 'create-recalc-button',
              onClick: () => setOpenCreateRecalcForm(true),
              text: 'Create',
              disabled: !canCreate
            }}
            maxHeight={600}
            pageSize={15}
          />
        </Box>
      )}
      {openCreateRecalcForm && (
        <RecalcForm
          stockId={stockId as string}
          handleClose={handleClose}
          historyRecalcInput={historyRecalc}
        />
      )}
    </Box>
  );
};

export default RecalcsTab;
